import {
  actionsKeys,
  capitalize,
  getRequiredFields,
  permissionActions,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { FieldValue, useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { schema } from "./validations";
import { toast } from "@/components/Toast";
import {
  ACCOUNT_NAME,
  AMOUNT,
  CHECK,
  CLEARING_AMOUNT,
  COMMUNITY_ID,
  DOC_DATE,
  DOC_NO,
  DOC_TYPE,
  DOCUMENT_TYPE,
  DRCR,
  DUE_DATE,
  GL_ACCOUNT,
  LINE_ITEM_ID,
  LINE_ITEMS,
  OWNERSHIP_ID,
  PAY_BLOCK,
  PAYMENT_METHOD,
  POSTING_MONTH,
  POSTING_YEAR,
  PROPERTY_ID,
  REMARK,
  SMALL_VARIANCE_GL,
  VARIANCE_AMT,
  DOCUMENT_ID,
} from "./consts";
import {
  useAddClearingDocMutation,
  useGetClearingOwnerList,
  useGetClearingPropertyList,
  useGetDocumentTypeDropdown,
  useGetGlMasterData,
  useGetPostedDocById,
  useGetUnclearLineItems,
} from "@/services";
import moment from "moment";
import { useQueryClient } from "@tanstack/react-query";

const useAddEdit = () => {
  const { clearingId, action } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [keys, setKeys] = useState({
    documentTypes: Math.random() * 2,
    drcr: Math.random() * 2,
    glList: Math.random() * 2,
    propertyList: Math.random() * 2,
    ownerList: Math.random() * 2,
  });
  const [isEntryView, setIsEntryView] = useState<boolean>(true);
  const isView = action == "view";

  const {communityData: communityDetail, currentFinanceYear, accessPermission} = useSelector(
    (state: RootState) => state.currentData
  );

  const form = useForm<any>({
    defaultValues: {
      [DOCUMENT_TYPE]: "",
      [DOCUMENT_ID]: "",
      [SMALL_VARIANCE_GL]: "",
      [VARIANCE_AMT]: "",
      [PROPERTY_ID]: "",
      [OWNERSHIP_ID]: "",
      [REMARK]: "",
      [COMMUNITY_ID]: communityDetail?.communityId?.toString(),
      [POSTING_YEAR]: currentFinanceYear?.toString(),
      [POSTING_MONTH]: "",
      [LINE_ITEMS]: [
        {
          [LINE_ITEM_ID]: "",
          [GL_ACCOUNT]: "",
          [DOC_DATE]: "",
          [DOC_NO]: "",
          [DOC_TYPE]: "",
          [DUE_DATE]: "",
          [PAYMENT_METHOD]: "",
          [AMOUNT]: "",
          [CLEARING_AMOUNT]: "",
          [PAY_BLOCK]: "",
          [DRCR]: "",
        },
      ],
    },
    resolver: zodResolver(schema),
  });

  const requiredFields = getRequiredFields(schema);

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.clearing}`,
        name: "Clearing",
      },
      ...(clearingId ? [{
        link: `/${routes.bom}/${routes.finance}/${routes.clearing}/${routes.listing}`,
        name: "Listing",
      }] : []),
      {
        link: "",
        name: (
          <span className="text-primary">
            {clearingId
              ? `${capitalize(action || "")} Clearing Doc`
              : "Add Clearing Doc"}
          </span>
        ),
      },
    ],
    []
  );

  const onBackClick = () => {
    navigate(
      `/${routes.bom}/${routes.finance}/${routes.clearing}/${routes.listing}`
    );
  };

  const breadcrumbTitle = clearingId
    ? `${capitalize(action || "")} Clearing Document - ${
        communityDetail?.communityName
      }`
    : `Add Clearing Document - ${communityDetail?.communityName}`;

  const hasPostPermission = useMemo(() => accessPermission?.[actionsKeys?.MANUAL_CLEARING?.toLowerCase()]?.[permissionActions.POST], [accessPermission]);

  const { data: documentTypes, isLoading: isDocumentTypeLoading } =
    useGetDocumentTypeDropdown({
      communityId: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const { data: glData, isLoading: isLoadingGlMaster } = useGetGlMasterData({
    communityId: communityDetail?.communityId,
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  const { data: propertyList, isLoading: isLoadingProperty } =
    useGetClearingPropertyList({
      communityId: communityDetail?.communityId,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const { data: ownerList, isLoading: isLoadingOwner } =
    useGetClearingOwnerList({
      propertyId: form.watch(PROPERTY_ID),
      communityId: communityDetail?.communityId,
      queryParams: {
        enabled: !!(communityDetail?.communityId && form.watch(PROPERTY_ID)),
      },
    });

  const { data: clearingDoc, isLoading: isClearingDoc, refetch: refetchClearingDoc } = useGetPostedDocById({
    id: clearingId as string,
    communityId: communityDetail?.communityId,
    entryView: isEntryView,
    queryParams: {
      enabled: !!(communityDetail?.communityId && clearingId),
    },
  });

  useEffect(() => {
    if (isView && clearingId) refetchClearingDoc();
  }, [isEntryView, isView, clearingId, refetchClearingDoc]);

  useEffect(() => {
    if (clearingDoc?.data) {
      const header = clearingDoc?.data?.header ;
      const postedLineItems = isEntryView
        ? clearingDoc?.data?.clearingTargetEntries
        : clearingDoc?.data?.lineItems;
      // const doc_id =  clearingId?.toString() || "";
      form.reset({
        [DOCUMENT_TYPE]: header?.[DOCUMENT_TYPE]?.toString() || "",
        [DOCUMENT_ID]:header?.[DOC_NO]?.toString() ||clearingId?.toString() ||"",
        [SMALL_VARIANCE_GL]:
          header?.varianceGlMasterId?.toString() || "",
        [ACCOUNT_NAME]:
          header?.varianceGlAccountName?.toString() || "",
        [VARIANCE_AMT]: header?.[VARIANCE_AMT]?.toString() || "",
        [PROPERTY_ID]: header?.[PROPERTY_ID]?.toString() || "",
        [OWNERSHIP_ID]: header?.[OWNERSHIP_ID]?.toString() || "",
        [REMARK]: header?.comments?.toString() || "",
        [COMMUNITY_ID]: communityDetail?.communityId?.toString(),
        [POSTING_YEAR]: currentFinanceYear?.toString(),
        [POSTING_MONTH]: header?.[POSTING_MONTH]?.toString() || "",
        [LINE_ITEMS]: (postedLineItems || [])?.map((val: any) => ({
          [LINE_ITEM_ID]: val?.[LINE_ITEM_ID]?.toString() || "",
          [GL_ACCOUNT]: val?.[GL_ACCOUNT]?.toString() || "",
          [DOC_DATE]: val?.[DOC_DATE] || "",
          [DOC_NO]: val?.[DOC_NO]?.toString() || "",
          [DOC_TYPE]: val?.[DOC_TYPE]?.toString() || "",
          [DUE_DATE]: val?.[DUE_DATE] || "",
          [PAYMENT_METHOD]: val?.[PAYMENT_METHOD]?.toString() || "",
          [AMOUNT]: val?.[AMOUNT]?.toString() || "",
          [CLEARING_AMOUNT]: val?.[AMOUNT]?.toString() || "",
          [PAY_BLOCK]: val?.[PAY_BLOCK] || "",
          [DRCR]: val?.[DRCR] || "",
          [CHECK]: false,
        })),
      });
      setKeys({
        documentTypes: Math.random() * 2,
        drcr: Math.random() * 2,
        glList: Math.random() * 2,
        propertyList: Math.random() * 2,
        ownerList: Math.random() * 2,
      });
    }
  }, [clearingDoc, isEntryView]);

  useEffect(() => {
    if (ownerList?.data)
      if (clearingDoc?.data) {
        setKeys({...keys, ownerList: Math.random() * 2,})
      } else {
        form.setValue(OWNERSHIP_ID, ownerList?.data?.[0]?.id?.toString());
      }
  }, [ownerList, clearingDoc]);

  const { data: unclearLineItems, isLoading: isLoadingLineItems } =
    useGetUnclearLineItems({
      communityId: communityDetail?.communityId,
      data: {
        filters: [
          {
            field: OWNERSHIP_ID,
            value: form.watch(OWNERSHIP_ID),
            operator: "EQUAL",
          },
          {
            field: PROPERTY_ID,
            value: form.watch(PROPERTY_ID),
            operator: "EQUAL",
          },
          {
            field: 'budgetYear',
            value: currentFinanceYear,
            operator: "EQUAL",
          },
        ],
        
        pageSize: 40,
        pageNumber: 0,
      },
      queryParams: {
        enabled: !!(
          communityDetail?.communityId &&
          form.watch(PROPERTY_ID) &&
          form.watch(OWNERSHIP_ID) &&
          !isView
        ),
      },
    });

  const { mutate: clearingInfo, isPending: isAddClearingPending } =
    useAddClearingDocMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["clearing-documents", communityDetail?.communityId],
        });
        onBackClick();
        toast.success("Clearing Document Added Successfully");
      },
    });

  useEffect(() => {
    if (unclearLineItems?.data) {
      form.setValue(
        LINE_ITEMS,
        unclearLineItems?.data?.map((val: any) => ({
          [LINE_ITEM_ID]: val?.[LINE_ITEM_ID]?.toString() || "",
          [GL_ACCOUNT]: val?.[GL_ACCOUNT]?.toString() || "",
          [DOC_DATE]: val?.[DOC_DATE] || "",
          [DOC_NO]: val?.[DOC_NO]?.toString() || "",
          [DOC_TYPE]: val?.[DOC_TYPE]?.toString() || "",
          [DUE_DATE]: val?.[DUE_DATE] || "",
          [PAYMENT_METHOD]: val?.[PAYMENT_METHOD]?.toString() || "",
          [AMOUNT]: val?.[AMOUNT]?.toString() || "",
          [CLEARING_AMOUNT]: val?.[AMOUNT]?.toString() || "",
          [PAY_BLOCK]: val?.[PAY_BLOCK] || "",
          [DRCR]: val?.[DRCR] || "",
          [CHECK]: false,
        }))
      );
    }
  }, [unclearLineItems]);

  useEffect(() => {
    if (documentTypes) {
      const documentTypeId = documentTypes
        ?.find((data: any) => {
          return data?.documentType?.toLowerCase() == "oc";
        })
        ?.documentTypeId?.toString();
      if (documentTypeId) {
        form.setValue(DOCUMENT_TYPE, documentTypeId);
        setKeys({ ...keys, documentTypes: Math.random() * 2 });
      } else {
        toast.error(
          "The Document Type 'OC' was not found. Kindly add it to proceed."
        );
        onBackClick();
      }
    }
  }, [documentTypes]);

  //Park
  const onSubmit = (data: FieldValue<any>) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    const lineItemsIds = (data[LINE_ITEMS] as any)
    ?.map((val: any) => val[CHECK] ? val[LINE_ITEM_ID] : "")
    .filter(Boolean);

    if (lineItemsIds?.length == 0) {
      toast.error("At least one line item is required", {
        className: "!bg-red-600 !text-white",
      });
      return;
    }

    if (Number(balanceAmount) != 0) {
      toast.error("Make sure the Balance Amount is 0.", {
        className: "!bg-red-600 !text-white",
      });
      return;
    }

    clearingInfo({
      communityId: communityDetail?.communityId,
      data: {
        ...data,
        [VARIANCE_AMT]: data?.[VARIANCE_AMT] || "0",
        [LINE_ITEMS]: lineItemsIds,
      },
    });
  };

  const lineItems = useWatch({ control: form.control, name: LINE_ITEMS });
  const varianceAmt = useWatch({ control: form.control, name: VARIANCE_AMT });

  const balanceAmount = useMemo(() => {
    if (Array.isArray(lineItems)) {
      const li = lineItems?.filter((data: any) => data?.lineItemType?.toLowerCase() != "offset");
      const drValues = li
        .filter(
          (item) =>
            item[CHECK] && item[DRCR]?.toLowerCase() == "dr" && item[CLEARING_AMOUNT]
        )
        .reduce((sum, item) => sum + Number(item[CLEARING_AMOUNT] || 0), 0);

      const crValues = li
        .filter(
          (item) =>
            item[CHECK] && item[DRCR]?.toLowerCase() == "cr" && item[CLEARING_AMOUNT]
        )
        .reduce((sum, item) => sum + Number(item[CLEARING_AMOUNT] || 0), 0);

      return drValues - crValues - varianceAmt;
    }
    return 0;
  }, [lineItems, varianceAmt]);

  const onPostingMonthChange = (value: any) => {
    form.setValue(POSTING_MONTH, value);
    form.setValue(POSTING_YEAR, moment(new Date(value)).format("YYYY"));
  };

  const onClearPostingMonth = () => {
    form.setValue(POSTING_MONTH, "");
  };

  return {
    breadcrumbData,
    onBackClick,
    form,
    onSubmit,
    requiredFields,
    onCancel: onBackClick,
    breadcrumbTitle,
    isDocumentTypeLoading,
    documentTypes,
    keys,
    glData,
    isLoadingGlMaster,
    isView,
    balanceAmount,
    isLoadingProperty,
    propertyList,
    isLoadingOwner,
    ownerList,
    onPostingMonthChange,
    onClearPostingMonth,
    isLoadingLineItems,
    isAddClearingPending,
    isClearingDoc,
    isEntryView,
    setIsEntryView,
    hasPostPermission
  };
};

export default useAddEdit;
